import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  CheckCircle2,
  Bot,
  Sparkles,
  LogIn,
  Inbox,
  Receipt,
  AlertCircle,
  TrendingUp,
  Calendar,
  FileText,
  Zap,
  Star,
  MessageSquare,
  Phone,
  Brain,
  Clock,
  Shield,
  Wallet,
} from "lucide-react";
import TierSelectionDialog, { SubscriptionTier } from "@/components/TierSelectionDialog";
import WatchSolChat from "@/components/Landing/WatchSolChat";
import { INDUSTRIES } from "@/lib/industries";

const Landing = () => {
  const navigate = useNavigate();
  const [showTierDialog, setShowTierDialog] = useState(false);

  const handleTierSelect = (tier: SubscriptionTier) => {
    localStorage.setItem("selectedTier", JSON.stringify({
      id: tier.id,
      name: tier.name,
      price: tier.price,
      priceId: tier.priceId,
      productId: tier.productId,
      selectedAt: new Date().toISOString(),
    }));
    setShowTierDialog(false);
    navigate("/free-trial");
  };

  const handleGetStarted = () => setShowTierDialog(true);

  const solJobs = [
    { icon: Inbox, title: "Answers every message", desc: "Text, email, DM, missed call — Sol replies in your voice within seconds." },
    { icon: Calendar, title: "Books the job", desc: "Negotiates times over SMS, checks your calendar, confirms the appointment." },
    { icon: FileText, title: "Writes the estimate", desc: "Reads the inquiry, builds a line-itemed estimate, sends it for approval." },
    { icon: Receipt, title: "Invoices when done", desc: "Auto-bills the client the moment the job is marked complete." },
    { icon: AlertCircle, title: "Chases overdue money", desc: "Polite reminder, firmer reminder, then a call script — until it's paid." },
    { icon: TrendingUp, title: "Runs your books", desc: "Categorizes transactions, reconciles feeds, writes your monthly P&L." },
  ];

  const tiers = [
    { name: "Starter", price: "$29", trial: "3 months FREE", features: ["Sol handles inbox + booking", "Unlimited clients", "Mobile PWA", "Email support"], highlight: false },
    { name: "Professional", price: "$79", trial: "6 months FREE", features: ["Everything in Starter", "Sol writes estimates + invoices", "Full accounting suite", "10 team seats"], highlight: true },
    { name: "Business", price: "$199", trial: "1 YEAR FREE", features: ["Everything in Pro", "Sol runs collections + P&L", "Unlimited team", "API + white-label"], highlight: false },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-primary p-1.5">
              <Sparkles className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">Detail Pro</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm">
            <a href="#meet-sol" className="text-muted-foreground hover:text-foreground">Meet Sol</a>
            <a href="#watch-sol" className="text-muted-foreground hover:text-foreground">Watch Sol work</a>
            <a href="#what-sol-does" className="text-muted-foreground hover:text-foreground">What Sol does</a>
            <a href="/pricing" className="text-muted-foreground hover:text-foreground">Pricing</a>
            <a href="/about" className="text-muted-foreground hover:text-foreground">About</a>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/auth")}>
              <LogIn className="h-4 w-4 mr-1" /> Sign In
            </Button>
            <Button size="sm" onClick={handleGetStarted}>Get Started</Button>
          </div>
        </div>
      </nav>

      {/* HERO — Sol front and center */}
      <section id="meet-sol" className="relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[700px] h-[700px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16 text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/30 rounded-full blur-2xl animate-pulse" />
              <div className="relative h-28 w-28 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-[0_0_80px_hsl(var(--primary)/0.5)] ring-4 ring-primary/20">
                <Sparkles className="h-14 w-14 text-primary-foreground" />
              </div>
              <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-emerald-500 border-4 border-background" />
            </div>
          </div>
          <Badge variant="secondary" className="mb-5">
            <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
            Sol is online — say hi
          </Badge>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-foreground leading-[1.05] mb-6">
            Meet <span className="text-primary">Sol</span>.<br />
            Your AI teammate<br />who never clocks out.
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Sol answers your messages, books your jobs, writes your estimates, sends your invoices, chases your money, and runs your books. All the work you keep meaning to hire someone for — done, 24/7, for less than a phone bill.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-4">
            <Button size="lg" onClick={handleGetStarted} className="group text-base">
              Set Sol up in 5 minutes
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button size="lg" variant="outline" asChild className="text-base">
              <a href="#watch-sol">
                <MessageSquare className="h-4 w-4 mr-2" />
                Talk to Sol now
              </a>
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">No credit card · Up to 1 year free · Cancel anytime</p>
        </div>
      </section>

      {/* WATCH SOL WORK — the star of the show */}
      <section id="watch-sol" className="py-16 md:py-24 bg-muted/30 border-y border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">
              <MessageSquare className="h-3 w-3 mr-1.5" /> Live demo
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight text-foreground mb-4">
              Send Sol a real inquiry.<br />
              <span className="text-primary">Watch Sol reply.</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              Pretend you're a customer. Ask Sol about a job. This is exactly how your customers will experience it.
            </p>
          </div>

          <WatchSolChat />

          <div className="text-center mt-8">
            <Button size="lg" onClick={handleGetStarted}>
              I want Sol on my business →
            </Button>
          </div>
        </div>
      </section>

      {/* WHAT SOL DOES — 6 jobs */}
      <section id="what-sol-does" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Badge variant="secondary" className="mb-4">
              <Bot className="h-3 w-3 mr-1.5" /> What Sol does
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
              Six jobs. One teammate.
            </h2>
            <p className="text-muted-foreground text-lg">
              You'd have to hire a receptionist, a bookkeeper, and an office manager to cover this. Sol does all three.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {solJobs.map((job) => (
              <div key={job.title} className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 transition-all">
                <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <job.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-1.5">{job.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{job.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOL'S BRAIN — why it's actually good */}
      <section className="py-20 md:py-28 bg-muted/30 border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="secondary" className="mb-4">
                <Brain className="h-3 w-3 mr-1.5" /> How Sol thinks
              </Badge>
              <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-5">
                Sol learns your business.<br />
                <span className="text-primary">Not just "AI".</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                Sol reads your past quotes, invoices, and messages. It picks up your pricing, your tone, your quirks — the way a good hire would after two weeks on the job. Except Sol gets there in five minutes.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: Clock, text: "Trained on your history — pricing, services, tone" },
                  { icon: MessageSquare, text: "Remembers every conversation with every client" },
                  { icon: Phone, text: "Handles voice, SMS, email, DMs — one brain, every channel" },
                  { icon: Shield, text: "Asks before sending anything that would surprise you" },
                ].map((item) => (
                  <li key={item.text} className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <item.icon className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <span className="text-foreground/90">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl">
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">Sol · Learning your business</div>
                    <div className="text-xs text-muted-foreground">Just now</div>
                  </div>
                </div>
                <div className="pt-4 space-y-3 text-sm">
                  {[
                    "✓ Learned 47 past estimates — average ticket $312",
                    "✓ Noticed you charge 1.5× for weekend jobs",
                    "✓ Detected your voice: friendly, no jargon, uses first names",
                    "✓ Set up auto-reply for after-hours inquiries",
                    "✓ Ready to handle inbox — awaiting your OK",
                  ].map((line, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border/50 animate-in fade-in slide-in-from-left"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <span>{line}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NUMBERS */}
      <section className="py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { n: "24/7", l: "Sol never sleeps" },
              { n: "< 60s", l: "Average reply time" },
              { n: "$3,400", l: "Avg. saved per month" },
              { n: "8 tools", l: "Replaced by Sol" },
            ].map((s) => (
              <div key={s.l}>
                <div className="text-4xl md:text-5xl font-black text-primary">{s.n}</div>
                <div className="text-sm text-muted-foreground mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-muted/30 border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-3">Operators who fired the busywork</Badge>
            <h2 className="text-3xl md:text-4xl font-bold">Sol replaces what you were about to hire.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "Sol replied to 47 leads over the weekend while I was camping. Booked 6 jobs. I made $3,200 without touching my phone.", name: "Mike R.", role: "Cleaning Service Owner" },
              { quote: "I was about to hire an office manager for $4k/mo. Sol does 90% of what she would've, for $79.", name: "Sarah L.", role: "Lawn Care Co." },
              { quote: "It writes estimates in my voice. Clients think it's me. I don't correct them.", name: "Devon K.", role: "Pressure Washing" },
            ].map((t) => (
              <div key={t.name} className="rounded-2xl bg-card border border-border p-6">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-sm mb-4 leading-relaxed">"{t.quote}"</p>
                <div className="text-xs pt-3 border-t border-border/60">
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-muted-foreground">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge className="mb-3 bg-primary text-primary-foreground">
              <Zap className="h-3 w-3 mr-1.5" /> Launch pricing
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-3">
              Hire Sol for less than your phone bill.
            </h2>
            <p className="text-muted-foreground text-lg">Up to a full year free. No credit card. Cancel in one click.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-2xl border p-6 flex flex-col ${tier.highlight ? "border-primary shadow-2xl shadow-primary/20 bg-card scale-105 relative" : "border-border bg-card"}`}
              >
                {tier.highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground">Most Popular</Badge>
                )}
                <div className="text-sm text-muted-foreground">{tier.name}</div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-4xl font-bold">{tier.price}</span>
                  <span className="text-muted-foreground">/mo</span>
                </div>
                <Badge variant="secondary" className="w-fit mt-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20">
                  {tier.trial}
                </Badge>
                <ul className="space-y-2 mt-5 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={tier.highlight ? "default" : "outline"}
                  onClick={handleGetStarted}
                  className="w-full"
                >
                  Start {tier.trial}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-16 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">Sol speaks your industry.</h2>
            <p className="text-muted-foreground text-sm">Pre-trained on the services, pricing, and language of 50+ trades.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
            {INDUSTRIES.map((i) => (
              <a
                key={i.slug}
                href={`/for/${i.slug}`}
                className="px-3 py-1.5 rounded-full bg-card border border-border text-sm text-foreground/80 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
              >
                {i.name}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Questions about Sol</h2>
          <div className="space-y-3">
            {[
              { q: "Is Sol actually good, or just a chatbot?", a: "Sol is trained on your business — your pricing, your past quotes, your tone. It's not a generic bot. Test it live above before you sign up." },
              { q: "What if Sol says something wrong?", a: "Sol asks before sending anything unusual (big quote, refund, weekend booking). You approve from your phone. Everything else is auto." },
              { q: "How long is the free trial?", a: "Starter is free for 3 months, Professional for 6 months, Business for a full year. No credit card required." },
              { q: "Will Sol talk to my customers over SMS and voice?", a: "Yes — text, email, DMs (Instagram/Meta/TikTok), and voice calls through Twilio. One brain, every channel." },
              { q: "Can I cancel anytime?", a: "One click from Settings → Billing. No calls, no retention scripts. Export your data anytime." },
              { q: "Does Sol replace my accountant?", a: "Sol runs your books — categorization, reconciliation, monthly P&L. Your accountant will love how clean everything is at tax time." },
            ].map((item) => (
              <details key={item.q} className="group rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-semibold flex items-center justify-between list-none">
                  {item.q}
                  <span className="text-muted-foreground group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 md:py-28 bg-muted/20 border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-4">
            Stop being the receptionist.<br />
            <span className="text-primary">Hire Sol.</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8">Set Sol up in 5 minutes. Free for up to a year.</p>
          <Button size="lg" onClick={handleGetStarted} className="group text-base">
            Set Sol up now
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="font-bold mb-3">Detail Pro Suite</div>
            <p className="text-muted-foreground text-xs">Sol — your AI teammate for service businesses.</p>
          </div>
          <div>
            <div className="font-semibold mb-3">Product</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="/features" className="hover:text-foreground">Features</a></li>
              <li><a href="/pricing" className="hover:text-foreground">Pricing</a></li>
              <li><a href="/switch-and-save" className="hover:text-foreground">Switch & Save</a></li>
              <li><a href="/migration-concierge" className="hover:text-foreground">Migration concierge</a></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3">Company</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="/about" className="hover:text-foreground">About</a></li>
              <li><a href="/founder" className="hover:text-foreground">Founder story</a></li>
              <li><a href="/contact" className="hover:text-foreground">Contact</a></li>
              <li><a href="/schedule-demo" className="hover:text-foreground">Book a demo</a></li>
            </ul>
          </div>
          <div>
            <div className="font-semibold mb-3">Legal</div>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="/trust" className="hover:text-foreground">Security & Trust</a></li>
              <li><a href="/privacy" className="hover:text-foreground">Privacy</a></li>
              <li><a href="/terms" className="hover:text-foreground">Terms</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 mt-8 text-xs text-muted-foreground text-center">
          © {new Date().getFullYear()} Detail Pro Suite. All rights reserved.
        </div>
      </footer>

      <TierSelectionDialog
        open={showTierDialog}
        onOpenChange={setShowTierDialog}
        onSelectTier={handleTierSelect}
      />
    </div>
  );
};

export default Landing;
